/**
 * Heuristics for SignalK Explorer → dashboard widget creation.
 */
import { formatSignalKNumeric, normalizeSignalkScalar } from './signalKValueFormat';

const READING_LABELS = {
  stateOfCharge: 'State of charge',
  timeRemaining: 'Time remaining',
  consumedCharge: 'Consumed charge',
  dischargedEnergy: 'Discharged energy',
  dischargeSinceFull: 'Since last full',
  lifetimeDischarge: 'Lifetime discharge',
  voltage: 'Voltage',
  current: 'Current',
  power: 'Power',
  frequency: 'Frequency',
  panelPower: 'Solar panel power',
  panelVoltage: 'Solar panel voltage',
  yieldToday: 'Solar yield today',
  yieldYesterday: 'Solar yield yesterday',
  systemYield: 'Lifetime solar yield',
  inverterMode: 'Inverter mode',
  chargingMode: 'Charging mode',
  operatingState: 'Operating state',
  currentLevel: 'Tank level',
  remaining: 'Remaining volume',
  capacity: 'Capacity',
  lineNeutralVoltage: 'AC voltage',
  realPower: 'AC power',
  speedOverGround: 'Speed over ground',
  courseOverGroundTrue: 'Course over ground',
  headingTrue: 'Heading',
  position: 'Position',
  depth: 'Depth',
  allowToCharge: 'BMS allow charge',
  allowToDischarge: 'BMS allow discharge',
};

/** Human label for a path suffix (e.g. capacity.stateOfCharge → State of charge). */
export function explorerReadingLabel(pathSuffix) {
  if (!pathSuffix) return 'Reading';
  const parts = pathSuffix.split('.');
  const leaf = parts[parts.length - 1];
  if (READING_LABELS[leaf]) return READING_LABELS[leaf];
  const label = leaf
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatDurationSeconds(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) {
    return { display: '—', unit: '' };
  }
  // Victron reports ~864000 s when full / unknown
  if (seconds >= 864000) return { display: 'Full', unit: '' };
  if (seconds >= 86400) {
    return { display: String(Math.round(seconds / 3600)), unit: 'hr' };
  }
  if (seconds >= 3600) {
    return { display: (seconds / 3600).toFixed(1), unit: 'hr' };
  }
  if (seconds >= 60) {
    return { display: String(Math.round(seconds / 60)), unit: 'min' };
  }
  return { display: String(Math.round(seconds)), unit: 'sec' };
}

function inferRatioPercent(path, num, units) {
  const u = String(units || '').toLowerCase();
  const p = path.toLowerCase();
  if (
    p.includes('stateofcharge') ||
    p.includes('currentlevel') ||
    p.endsWith('.level') ||
    u === 'ratio'
  ) {
    if (num >= -0.02 && num <= 1.05) return num * 100;
    if (num >= 0 && num <= 100) return num;
  }
  return null;
}

/**
 * Format a catalog row the way a dashboard widget would show it.
 * @returns {{ label: string, display: string, unit: string, hint: string }}
 */
export function formatExplorerReading(fullPath, rawValue, signalKUnits = '') {
  const path = String(fullPath || '');
  const suffix = path.includes('.') ? path.split('.').slice(-2).join('.') : path;
  const leaf = path.split('.').pop() || '';
  const label = explorerReadingLabel(
    leaf === 'stateOfCharge' || leaf === 'timeRemaining' ? `capacity.${leaf}` : leaf
  );

  const scalar = normalizeSignalkScalar(rawValue);
  if (scalar === null) {
    return { label, display: '—', unit: '', hint: '' };
  }

  if (typeof scalar === 'string' && Number.isNaN(parseFloat(scalar))) {
    const titled = scalar.charAt(0).toUpperCase() + scalar.slice(1);
    return { label, display: titled, unit: '', hint: '' };
  }

  const num = typeof scalar === 'number' ? scalar : parseFloat(scalar);
  if (!Number.isFinite(num)) {
    return { label, display: String(scalar), unit: signalKUnits || '', hint: '' };
  }

  const pathLower = path.toLowerCase();
  const unitsLower = String(signalKUnits || '').toLowerCase();

  const pct = inferRatioPercent(pathLower, num, unitsLower);
  if (pct !== null) {
    return { label, display: pct.toFixed(1), unit: '%', hint: '' };
  }

  if (pathLower.includes('timeremaining')) {
    if (num >= 86400) {
      return { label, display: 'Full', unit: '', hint: '' };
    }
    const d = formatDurationSeconds(num);
    return { label, display: d.display, unit: d.unit, hint: '' };
  }

  if (pathLower.includes('remaining') && unitsLower === 's') {
    const d = formatDurationSeconds(num);
    return { label, display: d.display, unit: d.unit, hint: '' };
  }

  if (pathLower.includes('runtime')) {
    const d = formatDurationSeconds(num);
    return { label, display: d.display, unit: d.unit, hint: '' };
  }

  if (pathLower.includes('yield') || pathLower.includes('energy') || unitsLower === 'j') {
    const kwh = num / 3600000;
    if (kwh >= 1) {
      return { label, display: kwh.toFixed(1), unit: 'kWh', hint: '' };
    }
    const wh = num / 3600;
    return { label, display: wh.toFixed(0), unit: 'Wh', hint: '' };
  }

  if (pathLower.includes('current') && !pathLower.includes('limit')) {
    const { value, unit } = formatSignalKNumeric(path, rawValue, {}, signalKUnits || 'A');
    let hint = '';
    if (num < -0.05) hint = 'Discharging';
    else if (num > 0.05) hint = 'Charging';
    return {
      label,
      display: String(value),
      unit: unit || 'A',
      hint,
    };
  }

  if (pathLower.includes('power')) {
    const { value, unit } = formatSignalKNumeric(path, rawValue, {}, signalKUnits || 'W');
    let hint = '';
    if (num < -0.5) hint = 'Power out';
    else if (num > 0.5) hint = 'Power in';
    return { label, display: String(value), unit: unit || 'W', hint };
  }

  const { value, unit } = formatSignalKNumeric(path, rawValue, {}, signalKUnits);
  return {
    label,
    display: String(value),
    unit: unit || signalKUnits || '',
    hint: '',
  };
}

/** Combined display string e.g. "99.6 %" or "13.54 V" */
export function formatExplorerDisplayString(fullPath, rawValue, signalKUnits = '') {
  const { display, unit, hint } = formatExplorerReading(fullPath, rawValue, signalKUnits);
  if (display === '—') return '—';
  const core = unit ? `${display} ${unit}` : display;
  return hint ? `${core} (${hint})` : core;
}

export function suggestWidgetType(path) {
  const p = String(path || '').toLowerCase();
  if (!p) return 'digital-display';
  if (p === 'navigation.position' || (p.includes('position') && p.includes('navigation'))) {
    return 'map';
  }
  if (p.includes('wind') && (p.includes('speed') || p.includes('direction'))) {
    return 'wind';
  }
  if (p.includes('heading') || p.includes('courseoverground')) {
    return 'compass';
  }
  if (
    p.includes('stateofcharge') ||
    p.includes('currentlevel') ||
    p.endsWith('.level')
  ) {
    return 'gauge';
  }
  if (
    p.includes('voltage') ||
    p.includes('current') ||
    p.includes('rpm') ||
    p.includes('revolutions') ||
    p.includes('temperature') ||
    p.includes('depth') ||
    p.includes('power') ||
    p.includes('frequency')
  ) {
    return 'gauge';
  }
  if (
    p.includes('mode') ||
    p.includes('.state') ||
    p.includes('charging') ||
    p.includes('operatingstate')
  ) {
    return 'status-indicator';
  }
  return 'digital-display';
}

export function suggestWidgetName(path) {
  const parts = String(path || '').split('.');
  if (!parts.length) return 'SignalK Value';
  const leaf = parts[parts.length - 1];
  const instance = parts.length >= 3 ? parts[parts.length - 2] : '';
  const group = parts.length >= 2 ? parts[parts.length - 3] || parts[0] : parts[0];
  const label = leaf
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim();
  const titled = label.charAt(0).toUpperCase() + label.slice(1);
  if (instance && !/^\d+$/.test(instance)) {
    return `${instance} ${titled}`;
  }
  if (instance) {
    return `${group} ${instance} ${titled}`;
  }
  return `${group} ${titled}`.trim();
}

export const EXPLORER_HIDDEN_CATEGORIES = new Set(['host', 'provider']);

export const CATEGORY_LABELS = {
  electrical: 'Electrical',
  navigation: 'Navigation',
  propulsion: 'Propulsion',
  environment: 'Environment',
  tanks: 'Tanks',
  sensors: 'Sensors',
  notifications: 'Notifications',
  host: 'Host / Cerbo',
  provider: 'Provider stats',
  design: 'Vessel design',
  communications: 'Communications',
  steering: 'Steering',
  ais: 'AIS',
  mmsi: 'MMSI',
  name: 'Vessel name',
};

/** Subgroup key e.g. electrical.batteries.279 from electrical.batteries.279.voltage */
export function catalogCollectionKey(path) {
  const parts = String(path || '').split('.').filter(Boolean);
  if (parts.length <= 2) return parts.join('.');
  const depth = Math.min(3, parts.length - 1);
  return parts.slice(0, depth).join('.');
}

export function catalogCollectionLabel(collectionKey) {
  const parts = collectionKey.split('.');
  if (parts.length <= 1) return collectionKey;
  return parts.slice(1).join(' · ');
}

export function catalogPathSuffix(path, collectionKey) {
  if (!path.startsWith(collectionKey + '.')) return path;
  return path.slice(collectionKey.length + 1);
}

/**
 * Group flat catalog rows into category → collection hierarchy for the explorer UI.
 * @returns {Array<{ category: string, label: string, collections: Array<{ key: string, label: string, paths: object[] }> }>}
 */
export function groupCatalogForExplorer(paths) {
  const byCategory = new Map();

  for (const row of paths) {
    const category = row.category || row.path.split('.')[0] || 'other';
    if (!byCategory.has(category)) {
      byCategory.set(category, new Map());
    }
    const collections = byCategory.get(category);
    const collKey = catalogCollectionKey(row.path);
    if (!collections.has(collKey)) {
      collections.set(collKey, []);
    }
    collections.get(collKey).push(row);
  }

  const categoryOrder = [
    'electrical',
    'navigation',
    'propulsion',
    'tanks',
    'environment',
    'sensors',
    'notifications',
    'design',
    'communications',
    'steering',
    'host',
    'provider',
  ];

  const sortedCategories = [...byCategory.keys()].sort((a, b) => {
    const ia = categoryOrder.indexOf(a);
    const ib = categoryOrder.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });

  return sortedCategories.map((category) => {
    const collectionsMap = byCategory.get(category);
    const collections = [...collectionsMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, collPaths]) => ({
        key,
        label: catalogCollectionLabel(key),
        paths: collPaths.sort((a, b) => a.path.localeCompare(b.path)),
      }));
    return {
      category,
      label: CATEGORY_LABELS[category] || category,
      collections,
      pathCount: collections.reduce((n, c) => n + c.paths.length, 0),
    };
  });
}

export function statusLabel(status) {
  if (status === 'live') return 'Live';
  if (status === 'stale') return 'Stale';
  if (status === 'archived') return 'Archived';
  return status || 'Unknown';
}

export function statusColor(status, theme) {
  if (status === 'live') return theme?.colors?.success?.main || '#22c55e';
  if (status === 'stale') return theme?.colors?.warning?.main || '#f59e0b';
  return theme?.colors?.text?.disabled || '#94a3b8';
}

/** Build tree structure from flat catalog paths for browse UI. */
export function buildCatalogTree(paths) {
  const tree = {};
  for (const entry of paths) {
    const parts = entry.path.split('.');
    let current = tree;
    parts.forEach((part, index) => {
      if (!current[part]) {
        current[part] = {
          children: {},
          isLeaf: index === parts.length - 1,
          path: index === parts.length - 1 ? entry.path : null,
          entry: index === parts.length - 1 ? entry : null,
        };
      } else if (index === parts.length - 1) {
        current[part].isLeaf = true;
        current[part].path = entry.path;
        current[part].entry = entry;
      }
      current = current[part].children;
    });
  }
  return tree;
}

export function formatCatalogValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    try {
      const s = JSON.stringify(value);
      return s.length > 80 ? `${s.slice(0, 77)}…` : s;
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  return String(value);
}
